import {makeAutoObservable} from 'mobx'
import CodeObject from './CodeObject'
import Institution from './Institution'

export class PaymentReport {
  id: number

  payDate: Date

  constructor(paymentReport: any) {
    this.id = paymentReport.Id
    this.payDate = paymentReport.PayDate
  }
}

export enum PaymentTab {
  CalcPayment = 1,
  Payments = 2,
  PaymentsExternal
}
export default class Payment {
  id: number

  date?: Date

  amount?: number

  finalAmount?: number

  month?: number

  numericYear?: number

  hebrewMonthName?: string

  textYear?: string

  comment?: string

  statusId?: number

  paymentReportId: number

  institutionId: number

  createdAt?: Date

  updatedAt?: Date

  status?: CodeObject

  createdBy?: number

  institution?: Institution

  paymentReport?: PaymentReport

  dollarRate?: number

  paymentPercent?: number

  paymentCapDollar?: number

  receivesDatotBudgetSnapshot?: boolean

  isFixedPaymentSnapshot?: boolean

  fixedPaymentAmountSnapshot?: number

  actualStipendPaidDate?: Date

  isEligibleByDate?: boolean

  fullDayEquivalentCount?: number

  notEligibleReasonId?: number

  overrideAmount?: number

  overrideReason?: string

  // Filled by the API for payments still open (calculated / on hold): what the month comes to if
  // calculated today, and whether that differs from the stored amount — i.e. the roster changed
  // after the payment was recorded. The stored amount itself is never rewritten.
  currentCalculatedAmount?: number

  currentFullDayEquivalentCount?: number

  isCalculationOutdated: boolean = false

  buckets?: PaymentStudentBucket[]

  constructor(payment: any, statuses?: CodeObject[]) {
    this.id = payment.Id
    this.amount = payment.Amount
    this.finalAmount = payment.FinalAmount ?? undefined
    this.month = payment.Month
    this.numericYear = payment.NumericYear ?? payment.Year
    this.hebrewMonthName = payment.HebrewMonthName
    this.textYear = payment.TextYear
    this.comment = payment.Comment
    this.statusId = payment.StatusId
    this.paymentReportId = payment.PaymentReportId
    this.institutionId = payment.InstitutionId
    this.institution = payment.Institution
      ? new Institution(payment.Institution)
      : undefined
    this.createdAt = new Date(payment.CreatedAt)
    this.createdBy = payment.CreatedBy
    this.updatedAt = new Date(payment.UpdatedAt) || undefined
    this.paymentReport = payment.PaymentReport
      ? new PaymentReport(payment.PaymentReport)
      : undefined
    if (statuses) this.status = this.findStatus(statuses, payment.StatusId)

    this.dollarRate = payment.DollarRate
    this.paymentPercent = payment.PaymentPercent
    this.paymentCapDollar = payment.PaymentCapDollar
    this.receivesDatotBudgetSnapshot = payment.ReceivesDatotBudgetSnapshot
    this.isFixedPaymentSnapshot = payment.IsFixedPaymentSnapshot
    this.fixedPaymentAmountSnapshot = payment.FixedPaymentAmountSnapshot
    this.actualStipendPaidDate = payment.ActualStipendPaidDate
      ? new Date(payment.ActualStipendPaidDate)
      : undefined
    this.isEligibleByDate = payment.IsEligibleByDate
    this.fullDayEquivalentCount = payment.FullDayEquivalentCount
    this.notEligibleReasonId = payment.NotEligibleReasonId
    this.overrideAmount = payment.OverrideAmount
    this.overrideReason = payment.OverrideReason
    this.currentCalculatedAmount = payment.CurrentCalculatedAmount ?? undefined
    this.currentFullDayEquivalentCount =
      payment.CurrentFullDayEquivalentCount ?? undefined
    this.isCalculationOutdated = payment.IsCalculationOutdated === true
    if (payment.Buckets && Array.isArray(payment.Buckets)) {
      this.buckets = payment.Buckets.map(
        (b: any) => new PaymentStudentBucket(b)
      )
    }

    makeAutoObservable(this)
  }

  private findStatus(
    statuses: CodeObject[],
    statusId?: number
  ): CodeObject | undefined {
    if (statusId === undefined || statusId === null) {
      return undefined
    }
    return statuses.find(status => status.id === statusId)
  }

  setStatus = (statusId: number | undefined) => {
    this.statusId = statusId
  }

  get isEditable() {
    return this.statusId === 1 || this.statusId === 3
  }

  get displayAmount() {
    return this.finalAmount ?? this.amount
  }
}

export class PaymentStudentBucket {
  id: number

  paymentId: number

  shiftId: number

  datotRateId?: number

  scholarship: number

  datotRateAmount: number

  studentCount: number

  perStudentAmount: number

  bucketTotalAmount: number

  label?: string

  morningScholarship?: number

  afternoonScholarship?: number

  constructor(b: any) {
    this.id = b.Id
    this.paymentId = b.PaymentId
    this.shiftId = b.ShiftId
    this.datotRateId = b.DatotRateId
    this.scholarship = b.Scholarship
    this.datotRateAmount = b.DatotRateAmount
    this.studentCount = b.StudentCount
    this.perStudentAmount = b.PerStudentAmount
    this.bucketTotalAmount = b.BucketTotalAmount
    this.label = b.Label
    this.morningScholarship = b.MorningScholarship
    this.afternoonScholarship = b.AfternoonScholarship
    makeAutoObservable(this)
  }
}

export enum NotEligibleReason {
  GregorianSkip = 1,
  LatePayment = 2,
  BelowFullDayThreshold = 3,
  StipendDateNotReported = 4
}

export const notEligibleText = (id?: number): string | null => {
  switch (id) {
    case NotEligibleReason.GregorianSkip:
      return 'מדולג בלוח לועזי'
    case NotEligibleReason.LatePayment:
      return 'תאריך תשלום באיחור (7 ימים או יותר)'
    case NotEligibleReason.BelowFullDayThreshold:
      return 'פחות מ-10 אברכים שווה-ערך'
    case NotEligibleReason.StipendDateNotReported:
      return 'תאריך תשלום מלגה לא עודכן'
    default:
      return null
  }
}

export enum StipendCalendarType {
  Hebrew = 1,
  Gregorian = 2
}
